// RKA paket, in a PBJ project's header (API /pbj/projects/:id/rka, migration
// 0038): one Rencana Kerja dan Anggaran per project, a PDF, Word or Excel
// file of up to 10 MB. Pluto reads its text when it drafts the documents of
// stage 0 and 1, which the block says. Without one: Unggah RKA. With one: its
// name, size and last change, Unduh, Ganti (the same picker) and Hapus (asked
// first). When its text could not be read (textChars 0 or textError, e.g. a
// scanned PDF) a warning says the AI cannot use it.
import { useEffect, useRef, useState } from "react";
import * as API from "../api";
import type { PbjRka } from "../api";
import { formatDateTime } from "../lib/format";
import { formatFileSize } from "../project/files";
import { Dialog, Notice, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";
import { saveFile } from "./docx";

export const RKA_MAX_BYTES = 10 * 1024 * 1024;
export const RKA_ACCEPT =
  ".pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const RKA_LEAD = "Pluto memakai isi RKA saat menyusun dokumen tahap Rencana Kerja & Anggaran dan Perencanaan project ini.";
export const RKA_UNREADABLE = "Teks RKA tidak terbaca, Pluto tidak bisa memakainya, misalnya PDF hasil scan.";

/** The problem with an RKA file, or null when it may be sent (the API checks again by content). */
export function checkRkaFile(file: File): string | null {
  const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".") + 1).toLowerCase() : "";
  if (!["pdf", "docx", "xlsx"].includes(ext)) return `${file.name} belum didukung. Unggah RKA berupa PDF, Word (.docx), atau Excel (.xlsx).`;
  if (file.size > RKA_MAX_BYTES) return `${file.name} berukuran ${formatFileSize(file.size)}, melebihi batas 10 MB.`;
  if (file.size === 0) return `${file.name} kosong.`;
  return null;
}

/** The RKA cannot feed the AI: no text was read from it. */
export function rkaUnreadable(rka: Pick<PbjRka, "textChars" | "textError">): boolean {
  return rka.textChars === 0 || rka.textError !== null;
}

type Action = "upload" | "download" | "delete" | null;

export function RkaBlock({ projectId }: { projectId: string }) {
  const loaded = useAsync<PbjRka | null>(() => API.getPbjRka(projectId), [projectId]);
  const [rka, setRka] = useState<PbjRka | null>(null);
  const [action, setAction] = useState<Action>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (loaded.data !== undefined) setRka(loaded.data);
  }, [loaded.data]);

  const act = async (kind: Exclude<Action, null>, work: () => Promise<void>) => {
    if (action) return;
    setAction(kind);
    setError(null);
    setDone(null);
    try {
      await work();
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setAction(null);
    }
  };

  const upload = (file: File) => {
    const problem = checkRkaFile(file);
    if (problem) {
      setDone(null);
      setError(problem);
      return;
    }
    void act("upload", async () => {
      const res = await API.uploadPbjRka(projectId, file);
      setRka(res.rka);
      setDone(res.replaced ? "RKA diganti dengan file baru." : "RKA tersimpan.");
    });
  };
  const download = () => void act("download", async () => saveFile(await API.downloadPbjRka(projectId)));
  const remove = () =>
    void act("delete", async () => {
      setConfirm(false);
      await API.deletePbjRka(projectId);
      setRka(null);
      setDone("RKA dihapus dari project ini.");
    });

  const loading = loaded.loading && loaded.data === undefined;
  return (
    <section className="pbj-rka" aria-labelledby="pbj-rka-title">
      <div className="pbj-rka-head">
        <h2 id="pbj-rka-title" className="pbj-subtitle">
          RKA paket
        </h2>
        <p className="pbj-rka-lead">{RKA_LEAD}</p>
      </div>
      {loading ? (
        <p className="pbj-next" role="status">
          Memuat RKA
        </p>
      ) : loaded.error && !rka ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>RKA belum dapat dimuat. {API.errorMessage(loaded.error)}</span>
        </p>
      ) : rka ? (
        <div className="pbj-rka-file">
          <span className="pbj-rka-name">
            <Glyph name="documentText" size={18} />
            <span>{rka.fileName}</span>
          </span>
          <span className="pbj-rka-meta num">
            {formatFileSize(rka.sizeBytes)}, diperbarui {formatDateTime(rka.updatedAt)}
          </span>
        </div>
      ) : (
        <p className="pbj-rka-meta">Belum ada RKA. Unggah PDF, Word, atau Excel, maksimal 10 MB.</p>
      )}
      {rka && rkaUnreadable(rka) ? (
        <Notice tone="warning" title="RKA belum dapat dipakai Pluto">
          <p>{RKA_UNREADABLE}</p>
          {rka.textError ? <p>{rka.textError}</p> : null}
        </Notice>
      ) : null}
      {loading ? null : (
        <div className="pbj-rka-actions">
          <button
            type="button"
            className={`btn btn-sm ${rka ? "btn-secondary" : "btn-primary"}`}
            disabled={action !== null}
            aria-busy={action === "upload" || undefined}
            onClick={() => input.current?.click()}
          >
            <Glyph name="paperclip" size={16} />
            <span>{action === "upload" ? "Mengunggah" : rka ? "Ganti RKA" : "Unggah RKA"}</span>
          </button>
          {rka ? (
            <>
              <button type="button" className="btn btn-secondary btn-sm" disabled={action !== null} aria-busy={action === "download" || undefined} onClick={download}>
                <Glyph name="documentDownload" size={16} />
                <span>{action === "download" ? "Menyiapkan" : "Unduh RKA"}</span>
              </button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={action !== null} aria-haspopup="dialog" onClick={() => setConfirm(true)}>
                <Glyph name="trash" size={16} />
                <span>Hapus RKA</span>
              </button>
            </>
          ) : null}
          <input
            ref={input}
            type="file"
            hidden
            accept={RKA_ACCEPT}
            aria-label="Pilih file RKA"
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              e.currentTarget.value = "";
              if (file) upload(file);
            }}
          />
        </div>
      )}
      {error ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{error}</span>
        </p>
      ) : done ? (
        <p className="pbj-rka-meta" role="status">
          {done}
        </p>
      ) : null}
      <Dialog
        open={confirm}
        size="sm"
        title="Hapus RKA?"
        onClose={() => setConfirm(false)}
        busy={action === "delete"}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirm(false)}>
              Batal
            </button>
            <button type="button" className="btn btn-primary" data-tone="danger" onClick={remove} disabled={action !== null}>
              Hapus RKA
            </button>
          </>
        }
      >
        <p className="dialog-text">
          {rka?.fileName} dihapus dari project ini. Dokumen tahap Rencana Kerja & Anggaran dan Perencanaan berikutnya disusun tanpa RKA sampai Anda mengunggahnya lagi.
        </p>
      </Dialog>
    </section>
  );
}
